/* import __COLOCATED_TEMPLATE__ from './main-redesign.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { states, objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { task as trackedTask } from 'ember-resources/util/ember-concurrency';

export default class MainRedesign extends Component {
  @service messengerSettingsTargetUserProvider;
  @service intl;
  @service helpCenterService;
  @service router;
  @service MessengerSettingsService;
  @service appService;
  @service outboundHomeService;
  @tracked showDisableSpaceModal = false;
  @tracked activeSpace;

  checklistSearchResult = trackedTask(this, taskFor(this.searchForLiveChecklists), () => []);

  get app() {
    return this.appService.app;
  }

  get model() {
    return this.args.model;
  }

  get spaces() {
    return this.model.spaces.selectedSpaces;
  }

  get isHelpCenterEnabled() {
    return this.helpCenterService?.site?.turnedOn;
  }

  @action
  isSpaceDisabledInDropdown(space) {
    if (space.isHelp && !space.enabled) {
      return !this.isHelpCenterEnabled;
    }
    if (space.isTasks && space.enabled && space.currentlyEnabledAndSetLive) {
      return this.hasLiveChecklists || this.isSearchingForLiveChecklists;
    }
    return false;
  }

  @action
  getDisabledReason(space) {
    if (space.isHelp && !space.enabled && !this.isHelpCenterEnabled) {
      if (this.app.canUseStandalone) {
        return this.intl.t('messenger.settings-layout.spaces.help.disabled-tooltip-standalone');
      } else {
        return this.intl.t(
          'messenger.settings-layout.spaces.help.disabled-tooltip-for-articles-sync',
          {
            url: this.router.urlFor('apps.app.articles.articles.list.all'),
            htmlSafe: true,
          },
        );
      }
    }
    if (space.isTasks && space.enabled && space.currentlyEnabledAndSetLive) {
      if (this.isSearchingForLiveChecklists) {
        return this.intl.t('messenger.settings-layout.spaces.tasks.checking-live-checklists');
      } else if (this.hasLiveChecklists) {
        return this.intl.t('messenger.settings-layout.spaces.tasks.has-live-checklists');
      }
    }
    return null;
  }

  get visibilitySettings() {
    return this.model.visibility;
  }

  get homeSettings() {
    return this.model.home;
  }

  get userTypeSettings() {
    return this.model.userType;
  }

  get learnMoreLink() {
    return 'https://www.intercom.com/help/en/collections/2094771-getting-started';
  }

  @action
  onSpacesReordered(reorderedSpaces) {
    let filteredSpaces = reorderedSpaces.filter((space) => !space.isHub);
    this.model.spaces.selectedSpaces = this._reconstructAllSpaces(filteredSpaces);
  }

  get reorderableSpaces() {
    let filteredSpaces = this.spaces.filter((space) => space.isReorderable && space.enabled);
    if (this.args.isMobilePlatform) {
      filteredSpaces = filteredSpaces.filter((space) => space.isAvailableForMobile);
    }
    return filteredSpaces;
  }

  get hub() {
    return this.spaces.find((space) => space.isHub);
  }

  get hasReorderableSpaces() {
    return this.reorderableSpaces.length > 0;
  }

  get enabledSpacesCount() {
    let enabledSpaces = this.spaces.filter((space) => space.enabled);
    if (this.args.isMobilePlatform) {
      enabledSpaces = enabledSpaces.filter((space) => space.isAvailableForMobile);
    }
    return enabledSpaces.length;
  }

  get hubShown() {
    return !this.args.isMobilePlatform && this.hub?.enabled;
  }

  get paywalledSpaces() {
    let filteredSpaces = this.spaces.filter((space) => space.isPaywalled);
    if (this.args.isMobilePlatform) {
      filteredSpaces = filteredSpaces.filter((space) => space.isAvailableForMobile);
    }
    return filteredSpaces;
  }

  get availableSpaces() {
    let filteredSpaces = this.spaces.filter(
      (space) => !space.enabled && !space.isPaywalled && (space.isReorderable || space.isHub),
    );
    if (this.args.isMobilePlatform) {
      filteredSpaces = filteredSpaces.filter((space) => space.isAvailableForMobile);
    }
    return filteredSpaces;
  }

  get availableSpacesDropdownOptions() {
    let allSpaces = this.spaces.filter((space) => space.type !== 'messages');
    if (this.args.isMobilePlatform) {
      allSpaces = allSpaces.filter((space) => space.isAvailableForMobile);
    }
    return allSpaces.map((space) => ({
      value: space.id,
      text: space.name,
      description: space.description,
      icon: space.icon,
      space,
      isSelected: space.enabled,
    }));
  }

  get allSpaces() {
    let allSpaces = this.spaces.filter((space) => space.type !== 'messages');

    if (this.args.isMobilePlatform) {
      allSpaces = allSpaces.filter((space) => space.isAvailableForMobile);
    }
    return allSpaces;
  }

  get hasAllSpaces() {
    return this.allSpaces.length > 0;
  }

  get selectedSpaceIds() {
    return this.availableSpacesDropdownOptions
      .filter((option) => option.isSelected)
      .map((option) => option.value);
  }

  get hasAvailableSpaces() {
    return this.availableSpacesDropdownOptions.length > 0;
  }

  get liveChecklistCount() {
    return this.checklistSearchResult.value?.contentWrappers.length ?? 0;
  }

  get hasLiveChecklists() {
    return Boolean(this.liveChecklistCount && this.liveChecklistCount > 0);
  }

  get isSearchingForLiveChecklists() {
    return taskFor(this.searchForLiveChecklists).isRunning;
  }

  @action
  onSpaceToggle(space) {
    if ((space.isNews || space.isHelp || space.isTasks || space.isTickets) && space.enabled) {
      this.activeSpace = space;
      if ((this.hub && this.hub.enabled) || space.isTasks || space.isTickets) {
        if (space.isHelp && this.hasHelpCenterSlot() && !this.app.canSearchBrowseWithoutHelpSpace) {
          this.showDisableSpaceModal = true;
        } else if (space.isNews && this.hasNewsSlot()) {
          this.showDisableSpaceModal = true;
        } else if (space.isTasks && this.hasTasksSlot()) {
          this.showDisableSpaceModal = true;
        } else if (space.isTickets) {
          this.showDisableSpaceModal = true;
        }
      }
      if (!this.showDisableSpaceModal) {
        this.removeActiveSpace();
      }
    } else {
      space.enabled = !space.enabled;
      if (space.isHub && !space.enabled) {
        this.homeSettings.disableRequireSearchBrowse();
      }
      if (space.isHelp && space.enabled) {
        this.homeSettings.slotGroup.addHelpCenterSlot();
      }

      if (space.isNews && space.enabled) {
        this.homeSettings.slotGroup.addNewsSlot();
      }

      if (space.isTasks && space.enabled) {
        this.homeSettings.slotGroup.addTasksSlot();
      }

      if (space.isHub && space.enabled && !this.homeSettings.slotGroup.hasNewConversationCardSlot) {
        this.homeSettings.slotGroup.addNewConversationCard();
      }
      this.trackSpaceToggle(space.enabled, space.name);
      if (space.isHub && space.enabled) {
        this.model.spaces.selectedSpaces = this._reconstructAllSpaces(this.reorderableSpaces);
      }
    }
    this.MessengerSettingsService.resetPreviewScreen();
  }

  @action
  trackSpaceToggle(enabled, spaceName) {
    this.MessengerSettingsService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'space',
      name: spaceName,
      toggled_to: enabled,
    });
  }

  hasHelpCenterSlot() {
    return this.homeSettings.slotGroup.hasSlot('help_center');
  }

  hasNewsSlot() {
    return this.homeSettings.slotGroup.hasSlot('news_items_card');
  }

  hasTasksSlot() {
    return this.homeSettings.slotGroup.hasSlot('checklists_card');
  }

  @action
  removeActiveSpace() {
    if (this.activeSpace.isNews) {
      this.homeSettings.slotGroup.removeNewsSlot();
    } else if (this.activeSpace.isHelp) {
      this.homeSettings.slotGroup.removeHelpCenterSlot();
    } else if (this.activeSpace.isTasks) {
      this.homeSettings.slotGroup.removeTasksSlot();
    }

    this.activeSpace.enabled = false;
    if (this.activeSpace.isHub && !this.activeSpace.enabled) {
      this.homeSettings.disableRequireSearchBrowse();
    }
    this.showDisableSpaceModal = false;
    this.trackSpaceToggle(false, this.activeSpace.name);
  }

  @action
  onSpaceSelectionChange(selectedIds) {
    let previouslySelected = this.selectedSpaceIds;
    let newlySelected = selectedIds.filter((id) => !previouslySelected.includes(id));
    let newlyDeselected = previouslySelected.filter((id) => !selectedIds.includes(id));

    newlySelected.forEach((spaceId) => {
      let space = this.spaces.find((s) => s.id === spaceId);
      if (space && !space.enabled) {
        this.onSpaceToggle(space);
      }
    });

    newlyDeselected.forEach((spaceId) => {
      let space = this.spaces.find((s) => s.id === spaceId);
      if (space && space.enabled) {
        this.onSpaceToggle(space);
      }
    });
  }

  @action
  onSpaceSelectionToggle(spaceOption, dropdown) {
    this.onSpaceToggle(spaceOption.space);

    if (dropdown && dropdown.hideDropdownOverlay) {
      dropdown.hideDropdownOverlay();
    }
  }

  _reconstructAllSpaces(reorderedSpaces) {
    let hubSpace = this.hub?.enabled ? [this.hub] : [];
    return [...hubSpace, ...reorderedSpaces, ...this.availableSpaces, ...this.paywalledSpaces];
  }

  @dropTask
  *searchForLiveChecklists() {
    return yield this.outboundHomeService.contentSearch({
      app_id: this.appService.app.id,
      object_types: [objectTypes.checklist],
      states: [states.live],
      per_page: 5,
    });
  }
}
